import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpenText, ArrowRight, ClipboardList, ShieldCheck, Users2, ChevronRight, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

const ROLE_LEVEL = { employee: 1, manager: 2, po_admin: 3, super_admin: 4 };

export default function LIFTHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const level = ROLE_LEVEL[user?.role || "employee"] || 1;

  const [participations, setParticipations] = useState([]);
  const [peerTasks, setPeerTasks] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeCycle = useMemo(() => cycles.find((c) => c.status === "active"), [cycles]);

  useEffect(() => {
    (async () => {
      try {
        const [mine, tasks, allCycles] = await Promise.all([
          api.get("/lift/my-participations"),
          api.get("/lift/peer-tasks"),
          api.get("/lift/cycles"),
        ]);
        setParticipations(mine.data);
        setPeerTasks(tasks.data);
        setCycles(allCycles.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="lift-home">
      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">
          Regular Rhythms, Honest Feedback, Hope For The Season Ahead.
        </h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          LIFT is our warm, twice-yearly review. Self-reflection, peer voices,
          and a manager summary — synthesised into one packet you can carry with you.
        </p>
      </div>

      {loading ? (
        <p className="italic text-charcoal-muted">Loading LIFT status…</p>
      ) : (
        <>
          <MyParticipations items={participations} onOpen={(cid) => navigate(`/lift/cycle/${cid}`)} />

          {peerTasks.length > 0 && (
            <PeerTaskCard tasks={peerTasks} />
          )}

          {level >= ROLE_LEVEL.manager && activeCycle && (
            <ManagerHubTeaser cycleId={activeCycle.id} />
          )}

          {level >= ROLE_LEVEL.po_admin && (
            <POActions />
          )}
        </>
      )}
    </div>
  );
}

function MyParticipations({ items, onOpen }) {
  if (!items.length) {
    return (
      <div className="patria-card p-8 text-center">
        <ClipboardList className="h-8 w-8 mx-auto text-forest-600" strokeWidth={1.5} />
        <p className="mt-3 text-charcoal-soft">
          You are not currently in an active LIFT cycle.
        </p>
      </div>
    );
  }
  return (
    <section data-testid="lift-my-participations" className="space-y-3">
      <h2 className="text-2xl font-serif text-forest-900">My LIFT</h2>
      {items.map((p) => (
        <button
          key={p.participation_id}
          onClick={() => onOpen(p.cycle_id)}
          className="patria-card patria-card-hover w-full p-6 text-left flex items-center gap-4"
          data-testid={`lift-my-participation-${p.cycle_id}`}
        >
          <span className="h-11 w-11 rounded-full bg-forest-600 text-cream-100 flex items-center justify-center">
            <BookOpenText className="h-5 w-5" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-serif text-forest-900">{p.cycle_name}</p>
            <p className="text-xs text-charcoal-muted">
              Cycle {p.cycle_status} · deadline {p.deadline}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className={p.status_self_reflection === "submitted" ? "patria-chip-green" : "patria-chip-terra"}>
                Self-reflection · {p.status_self_reflection?.replace("_", " ")}
              </span>
              {p.packet_released_at && (
                <span className="patria-chip-green">Packet released</span>
              )}
              {p.employee_ack_at && <span className="patria-chip-green">Acknowledged</span>}
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-forest-600" strokeWidth={1.5} />
        </button>
      ))}
    </section>
  );
}

function PeerTaskCard({ tasks }) {
  const pending = tasks.filter((t) => t.status === "pending").length;
  return (
    <Link
      to="/lift/peer-tasks"
      className="patria-card patria-card-hover block p-6"
      data-testid="lift-peer-tasks-card"
    >
      <div className="flex items-center gap-4">
        <span className="h-11 w-11 rounded-full bg-terracotta-500 text-cream-100 flex items-center justify-center">
          <Users2 className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <div className="flex-1">
          <p className="text-lg font-serif text-forest-900">Feedback Requests For Me</p>
          <p className="text-sm text-charcoal-soft">
            {pending > 0
              ? `${pending} colleague${pending > 1 ? "s have" : " has"} asked for your feedback.`
              : "You have no pending peer tasks right now."}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-forest-600" strokeWidth={1.5} />
      </div>
    </Link>
  );
}

function ManagerHubTeaser({ cycleId }) {
  return (
    <Link
      to={`/lift/manager/${cycleId}`}
      className="patria-card patria-card-hover block p-6"
      data-testid="lift-manager-hub-teaser"
    >
      <div className="flex items-center gap-4">
        <span className="h-11 w-11 rounded-full bg-forest-600 text-cream-100 flex items-center justify-center">
          <ClipboardCheck className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <div className="flex-1">
          <p className="text-lg font-serif text-forest-900">Manager Hub</p>
          <p className="text-sm text-charcoal-soft">
            Evaluate your direct reports, request peer feedback, and finalize packets.
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-forest-600" strokeWidth={1.5} />
      </div>
    </Link>
  );
}

function POActions() {
  return (
    <section data-testid="lift-po-actions" className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Link to="/lift/cycles" className="patria-card patria-card-hover p-6 block">
        <span className="patria-chip-green">People Operations</span>
        <h3 className="mt-3 text-xl font-serif text-forest-900">Cycles & Assignments</h3>
        <p className="mt-1 text-sm text-charcoal-soft">
          Create cycles, edit question templates, and assign participants.
        </p>
      </Link>
      <Link to="/lift/redaction-queue" className="patria-card patria-card-hover p-6 block" data-testid="lift-po-redaction-link">
        <span className="patria-chip-terra">Redaction queue</span>
        <h3 className="mt-3 text-xl font-serif text-forest-900">Peer Feedback Review</h3>
        <p className="mt-1 text-sm text-charcoal-soft">
          Approve free-text peer feedback before it&rsquo;s released anonymously to employees.
        </p>
      </Link>
    </section>
  );
}
